import React, {useState, useRef, useEffect} from 'react';
import {SafeAreaView, View, ActivityIndicator, ScrollView} from 'react-native';
import moment from 'moment';
import {useNavigation} from '@react-navigation/core';
import _ from 'lodash';
import {Divider} from 'react-native-paper';

import sy from '~/styles';
import styles from './styles';
import {
  service_request as serviceRequestApi,
  dealer as dealerApi,
  messages as messagesApi,
} from '~/api/public';
import withScreenIsFocused from '~/components/hoc/with-screen-is-focused';
import {
  Text,
  InputChat,
  ServiceRequestStatusIcon,
  Link,
} from '~/components/controls';
import {Message} from '~/components/shared';
import {STATUS_CODES, STATUS_DESCRIPTIONS} from '~/types/statuses';

import NotificationsIcon from '~/images/md-icons/notifications/materialicons/24px.svg';
import TextsmsIcon from '~/images/md-icons/textsms/materialicons/24px.svg';
import EmailIcon from '~/images/md-icons/email/materialicons/24px.svg';

const Activity = ({
  status,
  description,
  attributes,
  timestamp,
  rank,
  request,
}) => {
  if (attributes?.type === 'SMS') {
    return (
      <View
        style={[
          {
            backgroundColor: '#f2f2f2',
            borderRadius: 18,
          },
          sy['py-2'],
          sy['px-4'],
          sy['flex-row'],
          sy['items-center'],
          sy['gap-6'],
        ]}>
        <TextsmsIcon fill="#4a4a49" width={18} height={18} />
        <View
          style={[
            sy['flex-1'],
            sy['flex-row'],
            sy['justify-between'],
            {paddingRight: 6},
          ]}>
          <Text style={sy.smallMedium}>
            {status === 'SentFailed'
              ? 'SMS verzenden mislukt'
              : 'SMS verzonden'}
          </Text>
          <Text style={[sy.smallRegular]}>
            {moment(timestamp).format('H:mm')}
          </Text>
        </View>
      </View>
    );
  } else if (attributes?.type === 'Email') {
    return (
      <View
        style={[
          {
            backgroundColor: '#f2f2f2',
            borderRadius: 18,
          },
          sy['py-2'],
          sy['px-4'],
          sy['flex-row'],
          sy['items-center'],
          sy['gap-6'],
        ]}>
        <EmailIcon fill="#4a4a49" width={18} height={18} />
        <View
          style={[
            sy['flex-1'],
            sy['flex-row'],
            sy['justify-between'],
            {paddingRight: 6},
          ]}>
          <Text style={sy.smallMedium}>E-mail verzonden</Text>
          <Text style={[sy.smallRegular]}>
            {moment(timestamp).format('H:mm')}
          </Text>
        </View>
      </View>
    );
  }

  const navigation = useNavigation();
  const isCurrentStatus = request.status === status && rank === 1;

  let info = status;
  let icon = <NotificationsIcon style={{marginRight: 10}} />;
  let link = null;

  if (isCurrentStatus) {
    switch (status) {
      case STATUS_CODES.ApprovalRequested:
        link = (
          <Link
            textStyle={sy.xSmallMedium}
            onPress={() =>
              navigation.navigate('Confirm', {
                screen: 'Details',
                params: {request_id: request.id},
              })
            }>
            Beoordelen
          </Link>
        );
        break;
      case STATUS_CODES.ApprovalPending:
        link = (
          <Link
            textStyle={sy.xSmallMedium}
            onPress={() =>
              navigation.navigate('Confirm', {
                screen: 'Details',
                params: {request_id: request.id},
              })
            }>
            Beoordelen
          </Link>
        );
        break;
      case STATUS_CODES.Done:
        link = (
          <Link
            textStyle={sy.xSmallMedium}
            onPress={() =>
              navigation.navigate('Pickup', {
                screen: 'Details',
                params: {request_id: request.id},
              })
            }>
            Route &amp; overzicht
          </Link>
        );
        break;
      default:
        link = (
          <Link
            textStyle={sy.xSmallMedium}
            onPress={() =>
              navigation.navigate('Overview', {
                screen: 'Details',
                params: {request_id: request.id},
              })
            }>
            Bekijken
          </Link>
        );

        break;
    }
  }

  switch (status) {
    case STATUS_CODES.DateChanged:
    case STATUS_CODES.TimeChanged:
      info = `Update · ${STATUS_DESCRIPTIONS[status]}`;
      break;
    default:
      info = STATUS_DESCRIPTIONS[status];
      break;
  }

  if (description) {
    info += ` (${description})`;
  }

  icon = (
    <ServiceRequestStatusIcon
      status={status}
      iconProps={_.pickBy(
        {fill: !link && '#828282', width: 18, height: 18},
        _.identity,
      )}
    />
  );

  return (
    <View
      style={[
        {
          backgroundColor: '#f2f2f2',
          borderRadius: 18,
        },
        sy['py-2'],
        sy['px-4'],
        sy['flex-row'],
        sy['items-center'],
        sy['gap-6'],
      ]}>
      {icon}
      <View
        style={[
          sy['flex-1'],
          sy['flex-row'],
          sy['justify-between'],
          {paddingRight: 6},
        ]}>
        <Text
          style={[
            sy.smallMedium,
            {color: isCurrentStatus ? '#4a4a49' : '#828282'},
          ]}>
          {info}
          {link && <>&nbsp;&middot;&nbsp;{link}</>}
        </Text>
        <Text
          style={[
            sy.smallRegular,
            {color: isCurrentStatus ? '#4a4a49' : '#828282'},
          ]}>
          {moment(timestamp).format('HH:mm')}
        </Text>
      </View>
    </View>
  );
};

const Chat = ({navigation, route}) => {
  const request_id = route.params?.request_id;
  const [request, setRequest] = useState(null);
  const [messages, setMessages] = useState([]);
  const [loading, setLoading] = useState(true);
  const [pendingMessage, setPendingMessage] = useState('');

  const scrollViewRef = useRef(null);

  useEffect(() => {
    navigation.setOptions({
      headerShown: true,
    });

    const fetch = async () => {
      const request = await serviceRequestApi.overview(request_id);
      const dealer = await dealerApi.details(request.dealer_id);
      const messages = await messagesApi.list(request_id);

      setMessages(messages);
      setRequest(request);
      setLoading(false);
      navigation.setOptions({
        headerShown: true,
        headerStatusBarHeight: 0,
        headerTitleAlign: 'center',
        headerTitle: () => (
          <View>
            <Text style={sy.medium}>{dealer.name}</Text>
          </View>
        ),
      });
    };

    fetch();
  }, [request_id, navigation]);

  useEffect(() => {
    const intervalID = setInterval(async () => {
      const messages = await messagesApi.list(request_id);
      const request = await serviceRequestApi.overview(request_id);
      setMessages(messages);
      setRequest(request);
    }, 5000);

    return () => clearInterval(intervalID);
  }, [request_id]);

  const onImage = async (image) => {
    const {success} = await messagesApi.sendImage(request_id, image);
    if (success) {
      const newState = await messagesApi.list(request_id);
      setMessages(newState);
    }
  };

  const onSend = async () => {
    if (!pendingMessage.length) {
      return;
    }

    const {success} = await messagesApi.sendMessage(request_id, pendingMessage);
    if (success) {
      setPendingMessage('');

      const newState = await messagesApi.list(request_id);
      setMessages(newState);
    }
  };

  return (
    <SafeAreaView style={sy.mainView}>
      <Divider />
      <ScrollView
        style={[sy.contentView, sy['px-4'], sy['py-4']]}
        contentContainerStyle={[sy['gap-4']]}
        ref={scrollViewRef}
        onContentSizeChange={() =>
          scrollViewRef.current.scrollToEnd({animated: true})
        }>
        {loading && <ActivityIndicator size="large" color="#231fda" />}
        {!loading &&
          messages &&
          Object.keys(messages).map((key) => (
            <View key={key} style={[sy['gap-4'], styles.article]}>
              <Text style={[sy.smallPlus, sy['text-center']]}>
                {moment(key, 'DD-MM-YYYY').format('DD MMM YYYY')}
              </Text>
              {messages[key].map((message) =>
                message.message_id ? (
                  <Message key={message.message_id} {...message} />
                ) : (
                  <Activity
                    key={message.activity_id}
                    {...message}
                    request={request}
                  />
                ),
              )}
            </View>
          ))}
      </ScrollView>
      <View>
        <Divider />
        <InputChat
          message={pendingMessage}
          onChange={setPendingMessage}
          onSend={onSend}
          onImage={onImage}
        />
      </View>
    </SafeAreaView>
  );
};

export default withScreenIsFocused(Chat);
